import {
    Box,
    Typography,
    TextField,
    Button,
    Stack,
    Paper,
    useMediaQuery,
} from '@mui/material';
import { FC, useState } from 'react';
import { useContact } from '../courses/services/use-contact';
import { getRadientBG } from '../../utils/display';

const Page: FC = () => {
    const {
        mutate: sendContact,
        isPending,
        isSuccess,
        isError,
        error,
    } = useContact();

    const [form, setForm] = useState({
        name: '',
        email: '',
        message: '',
    });

    const handleChangeName = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setForm({ ...form, name: event.target.value });
    };

    const handleChangeEmail = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setForm({ ...form, email: event.target.value });
    };

    const handleChangeMessage = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setForm({ ...form, message: event.target.value });
    };

    const handleSubmit = async () => {
        sendContact(form);
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const isEmailValid = emailRegex.test(form.email);
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));


    return (
        <Box
            width="100%"
            height={'100%'}
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={2}
            boxSizing="border-box"
            sx={{ background: getRadientBG() }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: 720,
                    p: { xs: 3, md: 5 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Stack spacing={isMobile ? 0 : 2}>
                    {/* SUCCESS */}
                    {isSuccess ? (
                        <Box
                            textAlign="center"
                            py={4}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={2}
                        >
                            <Typography variant="h6" color="primary" fontWeight={700}>
                                Message envoyé ✅
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Nous reviendrons vers vous dans les plus brefs délais.
                            </Typography>

                            <Button
                                variant="outlined"
                                color="primary"
                                href="/"
                                sx={{
                                    mt: 1,
                                    borderRadius: 3,
                                    px: 3,
                                    fontWeight: 600,
                                    transition: '0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                Retour à l’accueil
                            </Button>
                        </Box>
                    ) : (
                        <>
                            {/* FORM */}
                            {/* HEADER */}
                            <Box textAlign="center">
                                <Typography
                                    variant="h5"
                                    color="secondary.main"
                                    fontWeight={700}
                                >
                                    Contact
                                </Typography>

                                <Typography variant="body2" color="text.secondary" mt={1}>
                                    Une question, un besoin ou un problème ? Notre équipe vous
                                    répond rapidement.
                                </Typography>
                            </Box>
                            <Stack spacing={2}>
                                <TextField
                                    label="Nom"
                                    value={form.name}
                                    onChange={handleChangeName}
                                    fullWidth
                                    inputProps={{ maxLength: 200 }}
                                />

                                <TextField
                                    label="Email"
                                    value={form.email}
                                    onChange={handleChangeEmail}
                                    fullWidth
                                    inputProps={{ maxLength: 200 }}
                                    error={form.email.length > 0 && !isEmailValid}
                                    helperText={
                                        form.email.length > 0 && !isEmailValid
                                            ? 'Adresse email invalide'
                                            : ''
                                    }
                                />

                                <TextField
                                    label="Message"
                                    value={form.message}
                                    onChange={handleChangeMessage}
                                    multiline
                                    minRows={4}
                                    fullWidth
                                    inputProps={{ maxLength: 2000 }}
                                    helperText={`${form.message.length}/2000`}
                                />
                            </Stack>

                            {/* BUTTON */}
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                disabled={
                                    isPending ||
                                    !form.email ||
                                    !form.message ||
                                    !form.name ||
                                    !isEmailValid
                                }
                                color="secondary"
                                sx={{ fontWeight: 600 }}
                            >
                                <Typography color="white" variant="subtitle1">
                                    {isPending ? 'Envoi...' : 'Envoyer le message'}
                                </Typography>
                            </Button>
                            {isError && (
                                <Box
                                    px={2}
                                    py={1.5}
                                    borderRadius={2}
                                    border="1px solid"
                                    borderColor="error.main"
                                >
                                    <Typography
                                        variant="body2"
                                        color="error"
                                        fontWeight={600}
                                        textAlign="center"
                                    >
                                        Une erreur est survenue lors de l’envoi du formulaire.
                                        Veuillez réessayer.
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};

export default Page;
